import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Link } from './Link';
import { Logo } from './Logo';

type MegaKey = 'locaties' | null;

const NAV_ITEMS: { label: string; href?: string; mega?: 'locaties'; match?: string[] }[] = [
  { label: 'Locaties', mega: 'locaties', match: ['/ten-boer', '/bedum'] },
  { label: 'Over ons', href: '/over-ons' },
  { label: 'Draag bij', href: '/draagbij' },
];

const LOCATIONS = [
  {
    href: '/ten-boer',
    name: 'Ten Boer',
    address: 'Reigerstraat / Kievitstraat',
    when: '14:30—16:00',
    image: '/images/hero-tenboer.jpg',
    est: 'EST. 2023',
  },
  {
    href: '/bedum',
    name: 'Bedum',
    address: 'De Kastanje 37',
    when: '14:30—16:00',
    image: '/images/hero-bedum.jpg',
    est: 'EST. 2025',
  },
];

/* ---------------- Icons ---------------- */
const ArrowIcon = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 12 12" fill="none">
    <path d="M2 6H10M10 6L6 2M10 6L6 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 12 12"
    fill="none"
    className={`transition-transform duration-300 ease-out ${open ? 'rotate-180' : ''}`}
  >
    <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* ---------------- Component ---------------- */
export const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mega, setMega] = useState<MegaKey>(null);
  const [mobileLocOpen, setMobileLocOpen] = useState(false);
  const getHashPath = () => {
    if (typeof window === 'undefined') return '/';
    const h = window.location.hash || '';
    return h.startsWith('#/') ? h.slice(1) || '/' : '/';
  };
  const [path, setPath] = useState(getHashPath);
  const closeTimer = useRef<number | null>(null);

  /* Scroll listener — only used for subtle scale/shadow boost */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* Track route changes via the hash */
  useEffect(() => {
    const update = () => {
      setPath(getHashPath());
      setMega(null);
    };
    window.addEventListener('hashchange', update);
    window.addEventListener('popstate', update);
    return () => {
      window.removeEventListener('hashchange', update);
      window.removeEventListener('popstate', update);
    };
  }, []);

  /* Escape closes menus */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMega(null);
        setMenuOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  /* Lock body scroll for mobile menu */
  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  const openMega = (key: MegaKey) => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setMega(key);
  };
  const scheduleClose = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setMega(null), 160);
  };

  const isActive = (item: (typeof NAV_ITEMS)[number]) =>
    item.href ? path === item.href : (item.match ?? []).includes(path);

  /* ----------- Desktop nav link ----------- */
  const NavLink = ({ item }: { item: (typeof NAV_ITEMS)[number] }) => {
    const active = isActive(item);
    const inner = (
      <span className="relative inline-flex items-center gap-1.5">
        {item.label}
        {item.mega && <ChevronIcon open={mega === item.mega} />}
        <span
          className={`absolute left-1/2 -translate-x-1/2 -bottom-2.5 w-1 h-1 rounded-full bg-[#F5B800] transition-opacity duration-200 ${
            active ? 'opacity-100' : 'opacity-0 group-hover:opacity-60'
          }`}
        />
      </span>
    );

    const cls =
      'group inline-flex items-center text-[14px] tracking-[-0.005em] font-normal text-white/85 hover:text-white transition-colors duration-200 py-1';

    if (item.mega) {
      return (
        <div className="relative" onMouseEnter={() => openMega(item.mega ?? null)}>
          <button
            className={cls}
            onClick={() => setMega(mega === item.mega ? null : item.mega ?? null)}
            aria-expanded={mega === item.mega}
            aria-haspopup="true"
          >
            {inner}
          </button>
        </div>
      );
    }
    return (
      <a href={item.href} onMouseEnter={scheduleClose} className={cls}>
        {inner}
      </a>
    );
  };

  /* ----------- Mobile menu ----------- */
  const mobileMenuNode = menuOpen ? (
    <div
      className="fixed inset-0 z-[9999] flex flex-col"
      style={{ background: '#0F4DF9' }}
    >
      {/* Soft yellow accent — matches the dark-section glow language */}
      <div
        className="absolute -top-40 -right-40 w-[420px] h-[420px] rounded-full opacity-[0.18] blur-3xl pointer-events-none"
        style={{ background: '#F5B800' }}
      />

      {/* Top bar — mirrors the floating pill's height + close button */}
      <div className="relative flex items-center justify-between px-6 sm:px-8 py-5 border-b border-white/15">
        <span className="text-white/55 font-mono text-[12px] tracking-[0.14em] uppercase">Menu</span>
        <button
          className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-white/20 text-white hover:bg-white/10 transition-colors active:scale-[0.96]"
          onClick={() => setMenuOpen(false)}
          aria-label="Menu sluiten"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      <nav className="relative flex-1 overflow-y-auto px-6 sm:px-8 py-6">
        <div className="flex flex-col divide-y divide-white/15">
          {/* Locaties accordion */}
          <div>
            <button
              onClick={() => setMobileLocOpen((v) => !v)}
              className="flex items-center justify-between w-full py-5 text-left"
            >
              <span className="text-white text-[34px] sm:text-[40px] font-medium tracking-[-0.02em]">
                Locaties
              </span>
              <span
                className={`inline-flex items-center justify-center w-9 h-9 rounded-full border border-white/25 text-white/85 transition-transform duration-300 ${
                  mobileLocOpen ? 'rotate-45 bg-[#F5B800] border-[#F5B800] text-[#0D0A00]' : ''
                }`}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </span>
            </button>
            <div
              className="grid transition-[grid-template-rows] duration-300 ease-out"
              style={{ gridTemplateRows: mobileLocOpen ? '1fr' : '0fr' }}
            >
              <div className="overflow-hidden">
                <div className="grid grid-cols-2 gap-3 pt-1 pb-5">
                  {LOCATIONS.map((loc) => (
                    <a
                      key={loc.href}
                      href={loc.href}
                      onClick={() => setMenuOpen(false)}
                      className="group relative rounded-2xl overflow-hidden border border-white/20 aspect-[4/5]"
                    >
                      <img
                        src={loc.image}
                        alt={loc.name}
                        className="absolute inset-0 w-full h-full object-cover opacity-75"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0F4DF9] via-[#0F4DF9]/55 to-[#0F4DF9]/10" />
                      <div className="absolute inset-0 p-4 flex flex-col justify-end">
                        <span className="text-[#F5B800] font-mono text-[10px] tracking-[0.14em]">{loc.est}</span>
                        <span className="text-white text-[20px] font-medium tracking-[-0.015em] mt-1">{loc.name}</span>
                        <span className="text-white/70 text-[12px] mt-0.5">{loc.address}</span>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {NAV_ITEMS.filter((i) => i.href).map((item) => {
            const active = item.href === path;
            return (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setMenuOpen(false)}
                className={`flex items-center justify-between py-5 text-[34px] sm:text-[40px] font-medium tracking-[-0.02em] transition-colors ${
                  active ? 'text-[#F5B800]' : 'text-white hover:text-[#F5B800]'
                }`}
              >
                <span className="inline-flex items-center gap-3">
                  {active && <span className="w-1.5 h-1.5 rounded-full bg-[#F5B800]" />}
                  {item.label}
                </span>
                <span className="text-white/55">
                  <ArrowIcon size={18} />
                </span>
              </a>
            );
          })}
        </div>

        {/* Contact CTA — yellow pill matching the desktop bar's Contact button */}
        <a
          href="/contact"
          onClick={() => setMenuOpen(false)}
          className="mt-8 flex items-center justify-between gap-4 rounded-full px-6 py-5 bg-[#F5B800] text-[#0D0A00] active:scale-[0.985] transition-transform"
        >
          <span className="text-[22px] font-medium tracking-[-0.015em]">Neem contact op</span>
          <ArrowIcon size={18} />
        </a>
      </nav>

      {/* Bottom utility — opening times + Instagram */}
      <div className="relative px-6 sm:px-8 py-5 border-t border-white/15 flex items-center justify-between">
        <span className="text-white/55 font-mono text-[11px] tracking-[0.14em]">
          ZONDAG · 14:30—16:00
        </span>
        <a
          href="https://www.instagram.com/sportcommunity_restart_tenboer/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-white/70 hover:text-white text-[13px] transition-colors"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="3" width="18" height="18" rx="5" stroke="currentColor" strokeWidth="1.6" />
            <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.6" />
            <circle cx="17.5" cy="6.5" r="1" fill="currentColor" />
          </svg>
          Instagram
        </a>
      </div>
    </div>
  ) : null;

  /* ----------- Backdrop (portal'd) ----------- */
  const backdropNode = mega ? (
    <motion.div
      key="mega-backdrop"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onMouseEnter={scheduleClose}
      onClick={() => setMega(null)}
      className="fixed inset-0 z-40 bg-[#0D0A00]/35 backdrop-blur-[2px]"
    />
  ) : null;

  /* ============================================================
     Layout philosophy: the header is a FIXED, always-floating
     pill. Consistent gutters on every side at every scroll
     position. Page content scrolls UNDER it. No morphing widths.
  ============================================================ */
  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 z-50 pointer-events-none"
        onMouseLeave={scheduleClose}
      >
        {/* Outer gutter — ALWAYS the same. The pill never touches the viewport edge. */}
        <div className="pointer-events-auto px-4 sm:px-6 pt-4 sm:pt-5">
          {/* The floating pill */}
          <motion.div
            initial={false}
            animate={{
              scale: scrolled ? 0.985 : 1,
              boxShadow: scrolled
                ? '0 22px 50px -22px rgba(13,10,0,0.55), inset 0 1px 0 rgba(255,255,255,0.14)'
                : '0 14px 30px -18px rgba(13,10,0,0.35), inset 0 1px 0 rgba(255,255,255,0.12)',
            }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="relative mx-auto max-w-6xl rounded-full border border-white/15 overflow-visible"
            style={{
              background: 'rgba(15,77,249,0.78)',
              backdropFilter: 'blur(22px) saturate(160%)',
              WebkitBackdropFilter: 'blur(22px) saturate(160%)',
              transformOrigin: 'center top',
            }}
          >
            {/* Top sheen — subtle refraction highlight */}
            <span
              aria-hidden
              className="pointer-events-none absolute inset-x-6 top-0 h-px rounded-full"
              style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.45), transparent)' }}
            />

            <div className="grid grid-cols-3 items-center px-4 sm:px-5 md:px-6 py-2.5 sm:py-3">
              {/* Left — desktop nav */}
              <nav className="hidden md:flex items-center gap-7 justify-start">
                {NAV_ITEMS.map((item) => (
                  <NavLink key={item.label} item={item} />
                ))}
              </nav>
              <div className="md:hidden" />

              {/* Center — logo */}
              <div className="flex justify-center">
                <Link href="/" aria-label="Home" className="inline-flex">
                  <Logo />
                </Link>
              </div>

              {/* Right — actions */}
              <div className="flex justify-end items-center gap-2.5">
                <a
                  href="/contact"
                  className={`hidden md:inline-flex items-center gap-2 rounded-full px-4 py-2 text-[13px] font-medium transition-all duration-200 active:scale-[0.97] hover:-translate-y-px ${
                    path === '/contact'
                      ? 'bg-white text-[#0D0A00]'
                      : 'bg-[#F5B800] text-[#0D0A00] hover:bg-[#FFC91F]'
                  }`}
                >
                  Contact
                  <ArrowIcon />
                </a>
                <button
                  className="md:hidden text-white focus:outline-none p-1.5 -mr-1"
                  onClick={() => setMenuOpen(true)}
                  aria-label="Menu openen"
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                    <line x1="4" y1="7" x2="20" y2="7" />
                    <line x1="4" y1="12" x2="20" y2="12" />
                    <line x1="4" y1="17" x2="20" y2="17" />
                  </svg>
                </button>
              </div>
            </div>
          </motion.div>

          {/* Mega menu — docks under the pill with the SAME gutter & max-width */}
          <AnimatePresence>
            {mega === 'locaties' && (
              <motion.div
                key="mega-locaties"
                initial={{ opacity: 0, y: -12, scale: 0.985 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.985 }}
                transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
                onMouseEnter={() => openMega('locaties')}
                onMouseLeave={scheduleClose}
                className="hidden md:block mx-auto max-w-6xl mt-3 origin-top"
              >
                <div
                  className="overflow-hidden rounded-[28px] border border-[#0D0A00]/8 bg-white relative"
                  style={{
                    boxShadow:
                      '0 30px 70px -25px rgba(13,10,0,0.4), 0 0 0 1px rgba(13,10,0,0.04)',
                  }}
                >
                  <div className="p-5 md:p-6">
                    <div className="flex items-baseline justify-between mb-4">
                      <span className="text-[#0D0A00]/45 font-mono text-[11px] tracking-[0.14em] uppercase">
                        Twee locaties · Groningen
                      </span>
                      <a
                        href="/contact"
                        className="text-[#0D0A00]/55 text-[13px] hover:text-[#0D0A00] inline-flex items-center gap-1.5 transition-colors"
                      >
                        Hulp nodig? <ArrowIcon />
                      </a>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {LOCATIONS.map((loc, i) => (
                        <motion.a
                          key={loc.href}
                          href={loc.href}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.06, duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                          className="group relative rounded-[20px] overflow-hidden border border-[#0D0A00]/8 bg-[#FBFAF6] aspect-[16/9]"
                        >
                          <img
                            src={loc.image}
                            alt={loc.name}
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.04]"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-[#0D0A00]/90 via-[#0D0A00]/40 to-transparent" />
                          <div className="absolute inset-0 p-5 md:p-6 flex items-end justify-between gap-4">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 mb-1.5">
                                <span className="text-[#F5B800] font-mono text-[10.5px] tracking-[0.16em]">{loc.est}</span>
                                <span className="w-1 h-1 rounded-full bg-white/40" />
                                <span className="text-white/65 font-mono text-[10.5px] tracking-[0.1em]">{loc.when}</span>
                              </div>
                              <h4 className="text-white text-[26px] font-medium tracking-[-0.02em] leading-tight">
                                {loc.name}
                              </h4>
                              <p className="text-white/70 text-[13px] mt-1">{loc.address}</p>
                            </div>
                            <span className="shrink-0 w-10 h-10 rounded-full bg-[#F5B800] text-[#0D0A00] flex items-center justify-center transition-transform duration-300 group-hover:translate-x-0.5 group-hover:rotate-[-45deg]">
                              <ArrowIcon />
                            </span>
                          </div>
                        </motion.a>
                      ))}
                    </div>

                    <div className="mt-5 pt-5 border-t border-[#0D0A00]/8 flex flex-wrap items-center justify-between gap-3">
                      <span className="text-[#0D0A00]/50 text-[13px]">
                        Niet zeker waar te beginnen?
                      </span>
                      <div className="flex flex-wrap items-center gap-3">
                        <a
                          href="/over-ons"
                          className="text-[#0D0A00] text-[13px] font-medium inline-flex items-center gap-1.5 hover:text-[#0F4DF9] transition-colors"
                        >
                          Lees ons verhaal <ArrowIcon />
                        </a>
                        <a
                          href="/contact"
                          className="text-[13px] font-medium inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 bg-[#0D0A00] text-white hover:-translate-y-px transition-transform"
                        >
                          Stel een vraag <ArrowIcon />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </header>

      {/* Portals */}
      {typeof document !== 'undefined' && backdropNode &&
        ReactDOM.createPortal(<AnimatePresence>{backdropNode}</AnimatePresence>, document.body)}
      {typeof document !== 'undefined' && mobileMenuNode &&
        ReactDOM.createPortal(mobileMenuNode, document.body)}
    </>
  );
};
